"use client";

import { truncateAddress } from "@aptos-labs/ts-sdk";
import { NotificationType } from "@decibeltrade/sdk";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";

import { toast } from "@/components/toast";
import { hasServerClaim } from "@/constants/chains";
import { readDexSdk } from "@/contexts/dex-sdk";
import { useNotifications } from "@/contexts/notification";
import { usdcBalanceQueryKey } from "@/contexts/queries/usdc-balance";
import { withTimeout } from "@/utils/abort";
import { addressComparisonKey, addressesEqual } from "@/utils/address";
import {
  checkAttestationReady,
  checkCCTPRedemptionStatus,
  checkCircleMessageReceived,
} from "@/utils/cctp/redemption";
import { logCctpUsage } from "@/utils/cctp/telemetry";
import { Logger } from "@/utils/logger";
import { NumberFormatter } from "@/utils/number-formatter";
import {
  findEntryByRequestId,
  getCancelReasonI18nKey,
  getProcessedTargetStep,
  sumProcessedByRequestId,
} from "@/utils/withdraw-queue-events";

import { bridgingUpdate, renewalPollOptions } from "./cctp-renewal";
import { createQueuedTracker, type QueuedTracker, queuedTransferKey } from "./queued-tracker";
import {
  isWithdrawTransfer,
  type TransferProgressData,
  type TransferStep,
} from "./transfer-progress-card";
import { transferTags } from "./transfer-telemetry";
import { useTransferProgress } from "./use-transfer-progress";

const BALANCE_POLL_INTERVAL_MS = 5_000;
const BRIDGE_POLL_INTERVAL_MS = 15_000;
const QUEUE_FALLBACK_POLL_INTERVAL_MS = 30_000;
const BALANCE_TOLERANCE = 0.99;
const STUCK_WARN_CYCLES = 10; // ~5 min at 30s intervals
const API_FAILURE_WARN_CYCLES = 10; // ~5 min of consecutive API failures

function showCancelledToast(
  cancelReason: string | null | undefined,
  transferProgressT: ReturnType<typeof useTranslations<"transferProgress">>,
) {
  toast({
    variant: "error",
    title: transferProgressT("steps.withdrawalCancelled"),
    description: transferProgressT(getCancelReasonI18nKey(cancelReason)),
  });
}

// ─── Shared terminal-state helpers (deduplicate WS + poll paths) ─────────────

interface TerminalDeps {
  updateTransferStep: (id: string, step: TransferStep) => void;
  updateTransfer: (id: string, fields: Partial<TransferProgressData>) => void;
  showProcessedToast: (transfer: TransferProgressData) => void;
  showCancelledToast: (cancelReason: string | null | undefined) => void;
  resetBalanceQueries: (userAddress: string) => void;
}

function advanceToProcessed(
  dedupKey: string,
  transfer: TransferProgressData & { type: "queued-withdraw" | "queued-xchain-withdraw" },
  tracker: QueuedTracker,
  deps: TerminalDeps,
) {
  if (tracker.isAdvanced(dedupKey)) return;
  tracker.advance(dedupKey);
  const targetStep = getProcessedTargetStep(transfer);
  deps.updateTransferStep(transfer.id, targetStep);
  if (targetStep === "available") {
    deps.showProcessedToast(transfer);
    deps.resetBalanceQueries(transfer.userAddress);
  }
}

function advanceToCancelled(
  dedupKey: string,
  transferId: string,
  cancelReason: string | null | undefined,
  tracker: QueuedTracker,
  deps: TerminalDeps,
) {
  if (tracker.isAdvanced(dedupKey)) return;
  tracker.advance(dedupKey);
  deps.updateTransfer(transferId, {
    currentStep: "withdrawal-cancelled",
    cancelReason: cancelReason ?? undefined,
  });
  deps.showCancelledToast(cancelReason);
}

function showToastForTransfer(
  t: TransferProgressData,
  generalT: ReturnType<typeof useTranslations<"general">>,
  transferProgressT: ReturnType<typeof useTranslations<"transferProgress">>,
  step: TransferStep,
) {
  const isWithdrawType = isWithdrawTransfer(t);
  const descriptions: Partial<Record<TransferStep, string>> = {
    claimable: "claimableToast",
    available: isWithdrawType ? "availableToast" : "availableDepositToast",
  };

  const descriptionTranslationKey = descriptions[step];
  if (!descriptionTranslationKey) return;
  toast({
    id: t.id,
    variant: "success",
    title: generalT("success"),
    description: transferProgressT(descriptionTranslationKey, {
      amount: NumberFormatter.usdc(t.amount),
      address: truncateAddress(t.destinationAddress),
      chain: t.destinationChain,
    }),
  });
}

/**
 * Provider-level hook that confirms transfer completion for ALL transfer types:
 *
 * ** Mesh (Aptos-native):** waits for Aptos tx confirmation or polls on-chain balance.
 *
 * ** CCTP (cross-chain deposit/withdraw):**
 *    - confirming → bridging: polls Circle API until message is received
 *    - bridging → available: polls Circle attestation API until ready
 *
 */
export function useTransferConfirmation() {
  const { transfers, updateTransferStep, updateTransfer } = useTransferProgress();
  const initialBalanceByTransferId = useRef<Record<string, number | undefined>>({});
  const waitingForTxByTransferId = useRef<Set<string>>(new Set());
  // Polling mutexes prevent interval-stacking when API calls take longer than the interval.
  const isBalancePollingRef = useRef(false);
  const isCctpConfirmPollingRef = useRef(false);
  const isCctpBridgePollingRef = useRef(false);
  const isCctpRedemptionPollingRef = useRef(false);
  const generalT = useTranslations("general");
  const transferProgressT = useTranslations("transferProgress");
  const queryClient = useQueryClient();
  const { avoidNotification } = useNotifications();

  // ─── Mesh: Aptos tx hash confirmation ───────────────────────────────────────
  useEffect(() => {
    const confirmingWithTxHash = transfers.filter(
      (t): t is TransferProgressData & { txHash: string } =>
        t.type === "mesh-deposit" &&
        t.currentStep === "confirming" &&
        !!t.txHash &&
        t.destinationChain === "Aptos",
    );

    for (const t of confirmingWithTxHash) {
      if (waitingForTxByTransferId.current.has(t.id)) continue;
      waitingForTxByTransferId.current.add(t.id);

      void (async () => {
        try {
          await readDexSdk.deps.aptos.waitForTransaction({
            transactionHash: t.txHash,
          });
          updateTransfer(t.id, { currentStep: "available", txHash: t.txHash });
          showToastForTransfer(t, generalT, transferProgressT, "available");
          void queryClient.resetQueries({
            queryKey: usdcBalanceQueryKey({ accountAddr: t.destinationAddress }),
          });
        } catch {
          // Tx not found or failed; remove so we could retry or fall back to balance polling
          waitingForTxByTransferId.current.delete(t.id);
        }
      })();
    }
  }, [transfers, updateTransfer, generalT, transferProgressT, queryClient]);

  // ─── Mesh: balance polling fallback (when txHash missing) ───────────────────
  useEffect(() => {
    const confirmingByBalance = transfers.filter(
      (t) =>
        t.type === "mesh-deposit" &&
        t.currentStep === "confirming" &&
        !t.txHash &&
        t.destinationChain === "Aptos",
    );

    if (confirmingByBalance.length === 0) return;

    const poll = async () => {
      if (isBalancePollingRef.current) return;
      isBalancePollingRef.current = true;
      try {
        for (const t of confirmingByBalance) {
          try {
            const balance = await readDexSdk.usdcBalance(t.destinationAddress);
            const key = t.id;
            let initial = t.initialBalance ?? initialBalanceByTransferId.current[key];
            if (initial === undefined) {
              initial = balance;
              initialBalanceByTransferId.current[key] = balance;
              updateTransfer(key, { initialBalance: balance });
            }
            const threshold = initial + t.amount * BALANCE_TOLERANCE;
            if (balance >= threshold) {
              updateTransferStep(t.id, "available");
              initialBalanceByTransferId.current[key] = undefined;
              showToastForTransfer(t, generalT, transferProgressT, "available");
              void queryClient.resetQueries({
                queryKey: usdcBalanceQueryKey({ accountAddr: t.destinationAddress }),
              });
            }
          } catch {
            // ignore per-transfer errors, will retry next interval
          }
        }
      } finally {
        isBalancePollingRef.current = false;
      }
    };

    void poll();
    const interval = setInterval(poll, BALANCE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [transfers, updateTransferStep, updateTransfer, generalT, transferProgressT, queryClient]);

  // ─── CCTP: confirming → bridging (Circle attestation API) ──────────────────
  // CCTP transactions are NOT indexed by Wormholescan operations API, so we
  // poll Circle's attestation API instead. A 200 response (even with PENDING
  // attestation) means the source tx was confirmed and Circle received it.
  useEffect(() => {
    const confirmingCCTP = transfers.filter(
      (
        t,
      ): t is TransferProgressData & {
        type: "cctp-deposit" | "cctp-withdraw";
        sourceTxHash: string;
      } =>
        (t.type === "cctp-deposit" || t.type === "cctp-withdraw") && t.currentStep === "confirming",
    );

    if (confirmingCCTP.length === 0) return;

    const poll = async () => {
      if (isCctpConfirmPollingRef.current) return;
      isCctpConfirmPollingRef.current = true;
      try {
        for (const t of confirmingCCTP) {
          try {
            const tags = transferTags(t);
            logCctpUsage(
              "status_poll",
              { ...tags, chain: t.sourceChain },
              { onceKey: t.sourceTxHash },
            );
            const received = await checkCircleMessageReceived(t.sourceTxHash, t.sourceChain, tags);
            if (received) {
              updateTransferStep(t.id, "bridging");
            }
          } catch {
            // retry next interval
          }
        }
      } finally {
        isCctpConfirmPollingRef.current = false;
      }
    };

    void poll();
    const interval = setInterval(poll, BRIDGE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [transfers, updateTransferStep]);

  // ─── CCTP deposit/withdraw: bridging → claimable (bridge completion) ─────────────
  // Polls Circle attestation API until the attestation is ready.
  // Advances to "claimable" so the user can manually claim on the destination chain.
  useEffect(() => {
    const bridgingWormhole = transfers.filter(
      (
        t,
      ): t is TransferProgressData & {
        type: "cctp-withdraw" | "cctp-deposit";
      } =>
        (t.type === "cctp-withdraw" || t.type === "cctp-deposit") && t.currentStep === "bridging",
    );

    if (bridgingWormhole.length === 0) return;

    const poll = async () => {
      if (isCctpBridgePollingRef.current) return;
      isCctpBridgePollingRef.current = true;
      try {
        for (const t of bridgingWormhole) {
          try {
            // Only deposits can be Fast, so only they carry speed and renewal state.
            const renewal = t.type === "cctp-deposit" ? t : {};
            const tags = transferTags(t);
            logCctpUsage(
              "status_poll",
              { ...tags, chain: t.sourceChain },
              { onceKey: t.sourceTxHash },
            );
            // Also check attestation as an earlier signal (Aptos → external chain)
            const response = await checkAttestationReady(t.sourceTxHash, t.sourceChain, {
              ...renewalPollOptions(renewal),
              transfer: tags,
            });
            const fieldsToUpdate = bridgingUpdate(renewal, response);
            if (!fieldsToUpdate) continue;
            // `bridgingUpdate` records a delay only when its reason changes, so this counts each once.
            if (fieldsToUpdate.delayReason) {
              logCctpUsage("fast_delayed", {
                ...tags,
                delayReason: fieldsToUpdate.delayReason,
                chain: t.sourceChain,
              });
            }
            updateTransfer(t.id, fieldsToUpdate);
            if (response.ready && !hasServerClaim(t.destinationChain)) {
              showToastForTransfer(t, generalT, transferProgressT, "claimable");
            }
          } catch {
            // retry next interval
          }
        }
      } finally {
        isCctpBridgePollingRef.current = false;
      }
    };

    void poll();
    const interval = setInterval(poll, BRIDGE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [transfers, updateTransfer, generalT, transferProgressT]);

  // ─── Queued withdrawals: WebSocket subscription ────────────────────────────
  const queuedTransfersRef = useRef<
    Array<TransferProgressData & { type: "queued-withdraw" | "queued-xchain-withdraw" }>
  >([]);
  const tracker = useRef(createQueuedTracker());

  // Shared deps for terminal-state helpers — avoids passing 6 args through every call site.
  const terminalDeps: TerminalDeps = useMemo(
    () => ({
      updateTransferStep,
      updateTransfer,
      showProcessedToast: (t: TransferProgressData) =>
        showToastForTransfer(t, generalT, transferProgressT, "available"),
      showCancelledToast: (reason: string | null | undefined) =>
        showCancelledToast(reason, transferProgressT),
      resetBalanceQueries: (userAddress: string) =>
        void queryClient.resetQueries({
          queryKey: usdcBalanceQueryKey({ accountAddr: userAddress }),
        }),
    }),
    [updateTransferStep, updateTransfer, generalT, transferProgressT, queryClient],
  );

  const queuedTransfers = useMemo(
    () =>
      transfers.filter(
        (t): t is TransferProgressData & { type: "queued-withdraw" | "queued-xchain-withdraw" } =>
          (t.type === "queued-withdraw" || t.type === "queued-xchain-withdraw") &&
          t.currentStep === "withdrawal-queued",
      ),
    [transfers],
  );
  // Assign inside useLayoutEffect so discarded concurrent renders don't leave a stale ref.
  useLayoutEffect(() => {
    queuedTransfersRef.current = queuedTransfers;
  }, [queuedTransfers]);

  // Suppress the global "Withdraw Successfully" notification while any queued-type
  // transfer cards exist (regardless of current step). Suppression ends when the user
  // dismisses the card, not when the step advances — otherwise the global notification
  // races with showToastForTransfer to produce a double toast.
  const hasQueuedTransfersForSuppression = transfers.some(
    (t) => t.type === "queued-withdraw" || t.type === "queued-xchain-withdraw",
  );
  // useLayoutEffect ensures suppression is installed synchronously before WS events
  // can fire during the first render cycle — useEffect would leave a gap where double
  // toasts could occur on page reload with queued transfers in localStorage.
  useLayoutEffect(() => {
    if (!hasQueuedTransfersForSuppression) return;
    return avoidNotification(NotificationType.AccountWithdrawal, Infinity);
  }, [hasQueuedTransfersForSuppression, avoidNotification]);

  // Only re-subscribe when the set of user addresses changes
  const queuedUserAddresses = useMemo(
    () => [...new Set(queuedTransfers.map((t) => t.userAddress))].sort().join(","),
    [queuedTransfers],
  );

  // Track previous address set for selective ref clearing
  const prevUserAddressesRef = useRef<string>("");

  useEffect(() => {
    if (!queuedUserAddresses) {
      prevUserAddressesRef.current = "";
      return;
    }

    // Only clear dedup keys for *removed* addresses — clearing everything would
    // wipe replay protection for still-active transfers.
    // Canonical, because the tracker keys are.
    const prevKeys = new Set(
      prevUserAddressesRef.current
        ? prevUserAddressesRef.current.split(",").map((a) => addressComparisonKey(a))
        : [],
    );
    const currentKeys = new Set(queuedUserAddresses.split(",").map((a) => addressComparisonKey(a)));
    const removedKeys = new Set([...prevKeys].filter((key) => !currentKeys.has(key)));
    prevUserAddressesRef.current = queuedUserAddresses;

    if (removedKeys.size > 0) {
      tracker.current.clearForAddresses(removedKeys);
    }

    const userAddresses = queuedUserAddresses.split(",");
    const unsubscribers: Array<() => void> = [];

    for (const userAddr of userAddresses) {
      const unsub = readDexSdk.withdrawQueue.subscribeByAddr(userAddr, (data) => {
        for (const entry of data.entries) {
          const match = queuedTransfersRef.current.find(
            (t) => t.requestId === entry.request_id && addressesEqual(t.userAddress, entry.user),
          );
          if (!match) continue;

          const dedupKey = queuedTransferKey(entry.user, entry.request_id);

          if (tracker.current.shouldSkipVersion(dedupKey, entry.transaction_version)) continue;
          tracker.current.setLastSeenVersion(dedupKey, entry.transaction_version);
          if (tracker.current.isAdvanced(dedupKey)) continue;

          if (entry.status === "Processed") {
            // Accumulate partial fills via tracker so two events in the same WS
            // batch don't both read stale React state.
            const latestMatch = queuedTransfersRef.current.find((t) => t.id === match.id);
            const prev =
              tracker.current.getWsAccumulated(dedupKey) ??
              (latestMatch && "processedAmount" in latestMatch
                ? (latestMatch.processedAmount ?? 0)
                : 0);
            const accumulated = prev + Math.max(0, entry.processed_amount);
            tracker.current.setWsAccumulated(dedupKey, accumulated);
            if (accumulated < match.amount - 1e-6) {
              Logger.warn("[withdraw-queue] Partial fill detected via WS", {
                requestId: entry.request_id,
                processed: accumulated,
                expected: match.amount,
              });
              updateTransfer(match.id, { processedAmount: accumulated });
              continue;
            }
            advanceToProcessed(dedupKey, match, tracker.current, terminalDeps);
          } else if (entry.status === "Cancelled") {
            advanceToCancelled(
              dedupKey,
              match.id,
              entry.cancel_reason,
              tracker.current,
              terminalDeps,
            );
          }
        }
      });
      unsubscribers.push(unsub);
    }

    return () => unsubscribers.forEach((fn) => fn());
  }, [queuedUserAddresses, updateTransfer, terminalDeps]);

  // ─── Queued withdrawals: fallback poll (reduced frequency for indexer lag) ──
  const hasQueuedTransfers = queuedTransfers.length > 0;
  const isPollingRef = useRef(false);

  useEffect(() => {
    if (!hasQueuedTransfers) return;

    const abortController = new AbortController();

    const poll = async () => {
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      try {
        const activeTransfers = queuedTransfersRef.current.filter(
          (t) =>
            t.currentStep === "withdrawal-queued" &&
            !tracker.current.isAdvanced(queuedTransferKey(t.userAddress, t.requestId)),
        );

        const uniqueAddresses = [
          ...new Set(activeTransfers.map((t) => t.userAddress.toLowerCase())),
        ];
        const pendingByUser = new Map<string, Array<{ request_id: string }>>();
        await Promise.all(
          uniqueAddresses.map(async (addr) => {
            try {
              const pending = await readDexSdk.withdrawQueue.getPendingWithdrawals(addr);
              pendingByUser.set(addr, pending);
              tracker.current.resetApiFailures(addr);
            } catch (error) {
              const failures = tracker.current.incrementApiFailures(addr);
              if (failures > 0 && failures % API_FAILURE_WARN_CYCLES === 0) {
                Logger.warn("[withdraw-queue] getPendingWithdrawals sustained failure", {
                  error,
                  userAddress: addr,
                  consecutiveFailures: failures,
                });
              }
            }
          }),
        );

        for (const t of activeTransfers) {
          const dedupKey = queuedTransferKey(t.userAddress, t.requestId);
          if (tracker.current.isAdvanced(dedupKey)) continue;

          try {
            const pending = pendingByUser.get(t.userAddress.toLowerCase());
            if (!pending) continue;

            const cycles = tracker.current.incrementPollCycles(dedupKey);
            if (cycles > 0 && cycles % STUCK_WARN_CYCLES === 0) {
              Logger.warn("[withdraw-queue] Transfer stuck in withdrawal-queued", {
                requestId: t.requestId,
                userAddress: t.userAddress,
                pollCycles: cycles,
                elapsedApproxMs: cycles * QUEUE_FALLBACK_POLL_INTERVAL_MS,
              });
            }
            const stillQueued = pending.some((p) => p.request_id === t.requestId);

            if (!stillQueued) {
              // Determine terminal status via indexed API (10s budget per search).
              const processedResult = await withTimeout(abortController.signal, (s) =>
                sumProcessedByRequestId(t.userAddress, t.requestId, s, t.amount),
              );

              if (processedResult.found) {
                if (tracker.current.isAdvanced(dedupKey)) continue;
                // Math.max to never regress WS-accumulated values during indexer lag.
                const currentProcessed = "processedAmount" in t ? (t.processedAmount ?? 0) : 0;
                const bestTotal = Math.max(processedResult.total, currentProcessed);
                if (bestTotal < t.amount - 1e-6) {
                  Logger.warn("[withdraw-queue] Partial fill detected via poll", {
                    requestId: t.requestId,
                    processed: bestTotal,
                    expected: t.amount,
                  });
                  updateTransfer(t.id, { processedAmount: bestTotal });
                  // Fall through to Cancelled check — remainder may have been cancelled.
                } else {
                  advanceToProcessed(dedupKey, t, tracker.current, terminalDeps);
                  continue;
                }
              }

              // Check for Cancelled
              if (tracker.current.isAdvanced(dedupKey)) continue;
              const cancelledMatch = await withTimeout(abortController.signal, (s) =>
                findEntryByRequestId(t.userAddress, "Cancelled", t.requestId, s),
              );

              if (cancelledMatch) {
                advanceToCancelled(
                  dedupKey,
                  t.id,
                  cancelledMatch.cancel_reason,
                  tracker.current,
                  terminalDeps,
                );
                continue;
              }

              // Entry not yet indexed — retry next interval
            }
          } catch (error) {
            Logger.warn("[withdraw-queue] fallback poll error", { error });
          }
        }
      } finally {
        isPollingRef.current = false;
      }
    };

    void poll();
    const interval = setInterval(poll, QUEUE_FALLBACK_POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      abortController.abort();
    };
  }, [hasQueuedTransfers, updateTransfer, terminalDeps]);

  // ─── CCTP deposit: claimable → (available/completed) (BFF redemption check) ─────────────
  // CCTP is NOT indexed by Wormholescan operations API. Instead, we check the
  // Wormholescan BFF endpoint to detect when the CCTP message has been redeemed
  // (claimed) on Aptos. When redeemed, funds are already in the wallet.
  useEffect(() => {
    const bridgingCCTP = transfers.filter(
      (
        t,
      ): t is TransferProgressData & {
        type: "cctp-deposit" | "cctp-withdraw";
      } =>
        (t.type === "cctp-deposit" || t.type === "cctp-withdraw") && t.currentStep === "claimable",
    );

    if (bridgingCCTP.length === 0) return;

    const poll = async () => {
      if (isCctpRedemptionPollingRef.current) return;
      isCctpRedemptionPollingRef.current = true;
      try {
        for (const t of bridgingCCTP) {
          try {
            const tags = transferTags(t);
            logCctpUsage(
              "status_poll",
              { ...tags, chain: t.sourceChain },
              { onceKey: t.sourceTxHash },
            );
            const result = await checkCCTPRedemptionStatus(
              t.sourceTxHash,
              t.sourceChain,
              t.messageBytes,
              tags,
            );
            if (!result.isCompleted) continue;
            // For withdrawals, we dont need to perform any futher actions after available, so we can mark as completed
            const stepToUpdateTo = t.type === "cctp-deposit" ? "available" : "completed";
            updateTransfer(t.id, {
              currentStep: stepToUpdateTo,
              ...(result.destinationTxHash && { destinationTxHash: result.destinationTxHash }),
            });
            showToastForTransfer(t, generalT, transferProgressT, stepToUpdateTo);
          } catch {
            // retry next interval
          }
        }
      } finally {
        isCctpRedemptionPollingRef.current = false;
      }
    };

    void poll();
    const interval = setInterval(poll, BRIDGE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [transfers, updateTransfer, generalT, transferProgressT]);
}
